import { Injectable, inject, OnDestroy } from '@angular/core';
import { MockService } from '../../mocks/mock.service';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { RoleService } from '../role/role.service';
import { UserRole } from './auth.role';
import { setAuthMockMode, getMockedDisplayName } from '../../mocks/data/auth/mocked-auth';

/**
 * Mock authentication service for development.
 * Implements AuthService using MockService to simulate authentication.
 */
@Injectable()
export class MockAuthService extends AuthService implements OnDestroy {
  private mockService = inject(MockService);
  private router = inject(Router);
  private roleService = inject(RoleService);
  private mockModeSubscription?: Subscription;

  override readonly isLoggedIn$ = new BehaviorSubject<boolean>(
    this.mockService.isLoggedIn()
  );
  override readonly isAdmin$ = new BehaviorSubject<boolean>(false);
  override readonly userRoles$ = new BehaviorSubject<string[]>(this.mockService.getMockedUserRoles());
  override readonly displayName$ = new BehaviorSubject<string>(getMockedDisplayName());

  constructor() {
    super();
    this.setupMockModeListener();
  }

  private setupMockModeListener(): void {
    this.mockModeSubscription = this.mockService.getAuthMockMode$().subscribe(() => {
      this.updateLoginStatus();
      this.updateAdminStatus();
      this.updateUserRoles();
      this.updateDisplayName();
    });
  }

  override getIsLoggedIn$(): BehaviorSubject<boolean> {
    return this.isLoggedIn$;
  }

  override getIsAdmin$(): Observable<boolean> {
    return this.isAdmin$;
  }

  override hasValidAccessToken(): boolean {
    return this.mockService.isLoggedIn();
  }

  override getAccessToken(): string | null {
    // Mock service doesn't provide access token
    return null;
  }

  override init(): void {
    // Initialize roles for mock mode
    this.roleService.updateRolesFromToken();
    this.updateLoginStatus();
    this.updateAdminStatus();
  }

  override login(): void {
    // Mock mode login - switch to logged-in user state via the mock-data-switcher
    setAuthMockMode('logged-in-user');
  }

  override logout(): void {
    // Mock mode: switch to logged-out state via the mock-data-switcher
    setAuthMockMode('logged-out');
  }

  // Private methods

  private updateLoginStatus(): void {
    this.isLoggedIn$.next(this.mockService.isLoggedIn());
  }

  private updateAdminStatus(): void {
    const roles = this.mockService.getMockedUserRoles();
    this.isAdmin$.next(roles.includes(UserRole.OrganizationAdmin) || roles.includes(UserRole.DataspaceAuthority));
  }

  private updateUserRoles(): void {
    const roles = this.mockService.getMockedUserRoles();
    this.userRoles$.next(roles);
  }

  private updateDisplayName(): void {
    this.displayName$.next(getMockedDisplayName());
  }

  ngOnDestroy(): void {
    this.mockModeSubscription?.unsubscribe();
  }
}
